import * as React from 'react';

import { ColoursPanelProps, StockCountProps, FormattedColour } from './ColoursPanel.types';
import { coloursSort, customColours } from './ColoursPanel.data';
import { StepsPanel } from '../StepsPanel';
import { Swatch } from '../Swatch';
import './styles/ColoursPanel.css';
import { ColorStock, Finish } from '../TitleBadge/TitleBadge.types';
import { getStockOrder } from '../../utils/helpers';

export const ColoursPanel = ({
    option,
    onSelect,
    value,
    step,
    title,
    stock,
    finish,
}: ColoursPanelProps) => {
    // Set up
    const [ currentVal, setCurrentVal ] = React.useState(value);
    const [ customColor, setCustomColor ] = React.useState('');
    const [ currFinish, setCurrFinish ] = React.useState(finish);

    const {
        name,
        values
    } = option;

    const standardColours = values.filter(
        val => val !== 'Custom Colour'
    ).map(val => {
        return { 
            value: val,
            customColour: false
        }
    });

    const custom = customColours.map(val => ({ value: val, customColour: true }));
    const colours = [...standardColours];
    if (values.includes('Custom Colour')) {
        colours.push(...custom)
    }

    React.useEffect(() => {
        setCurrentVal(value);
    }, [value]);

    React.useEffect(() => {
        setCurrFinish(finish);
    }, [finish]);

    // Methods
    const sortArray = (
        arr: FormattedColour[] | ColorStock[],
        sortArr: string[]
    ): FormattedColour[] | ColorStock[] => {
        let sortedVals: any = [];
        sortArr.forEach((key: string) => {
            let found = false;
            (arr as any).filter((val: FormattedColour | ColorStock) => {
                const color = (val as ColorStock).color ? (val as ColorStock).color : (val as FormattedColour).value;
                if(!found && key == color) {
                    sortedVals.push(val);
                    found = true;
                    return false;
                } else {
                    return true;
                }
            });
        });
        return sortedVals;
    };

    const onClickHandler = (colour: FormattedColour,) => { 
        setCurrentVal(colour.value);
        setCustomColor(colour.customColour ? colour.value : '');
        onSelect({
            name,
            value: colour.customColour ? 'Custom Colour': colour.value,
            colour: colour.value,
            step: step,
        });
    };

    const getColourSorting = (metal = false) => {
        let standardColourStock;
        let customColourStock: ColorStock[];
        if (metal) {
            standardColourStock = stock.filter(stockItem => {
                return stockItem.color !== 'Custom Colour' && stockItem.color.includes('Metal')
            });
            customColourStock = customColours.filter((colour => {
                return colour.includes('Metal');
            })).map(colour => {
                return {
                    color: colour,
                    stock: '0',
                };
            });
        }

        if (!metal) {
            standardColourStock = stock.filter(stockItem => {
                return stockItem.color !== 'Custom Colour' && !stockItem.color.includes('Metal')
            });
            customColourStock = customColours.filter((colour => {
                return !colour.includes('Metal');
            })).map(colour => {
                return {
                    color: colour,
                    stock: '0',
                };
            });
        }
        
        const hasStock = stock && stock.length > 0;
        if (hasStock) {
            return getStockOrder([
                ...standardColourStock, ...customColourStock
            ]);
        };
        return coloursSort;
    };

    const colourValues = () => {
        const metal = colours.filter(colour => colour.value.includes('Metal'));
        const nonMetal = colours.filter(colour => !colour.value.includes('Metal'));
        const metalSorting = getColourSorting(true);
        const nonMetalSorting = getColourSorting()
        return [
            [...sortArray(nonMetal, nonMetalSorting)],
            [...sortArray(metal, metalSorting)],
        ];
    };

    const getStockLevel = (colour: string) => {
        const match = stock.find(stockItem => stockItem.color === colour);
        if (match) return match.stock;
        return '0';
    }

    const sortedVals = colourValues();

    if(stock && stock.length > 0) {
        stock = [...sortArray(stock, getColourSorting())] as ColorStock[];
    }

    const subTitle = (
        <div className="stock-key">
            <div className="stock-key__item">
                <div
                    className="option__stock-count option__stock-count--in-stock"
                    style={{ position: 'static' }}
                />
                <div>
                    <p>In stock. Same day despatch*</p>
                    <p><small>*If ordered before 1pm (Mon-Thu)/11am (Fri)</small></p>
                </div>
            </div>
            <div className="stock-key__item">
                <div
                    className="option__stock-count"
                    style={{ position: 'static' }}
                />
                <div>
                    <p>No stock</p>
                    <p><small>3-4 week delivery</small></p>
                </div>
            </div>
        </div>
    );

    return (
        <StepsPanel 
            className={`options ${name.toLowerCase()}`}
            step={step}
            title={`Choose your ${title}`}
            subTitle={subTitle}
        >
            {sortedVals.map((colourSet, index) => (
                <React.Fragment key={`option-${index}`}>
                    {index === 1 && (
                        <h4>Metal Colours</h4>
                    )}
                    <div className="swatch-container">
                        {colourSet.map((colour: FormattedColour, index: number) => (
                            <div
                                key={`OptionPanel${name}Slide${index}`}
                                tabIndex={-2}
                                className="swatch-wrapper"
                            >
                                <Swatch
                                    value={colour.value}
                                    selected={currentVal === colour.value}
                                    onClick={() => onClickHandler(colour)}
                                    caption={colour.value.replace(/[\(\d)']+/g, '')}
                                />
                                <StockCount stock={getStockLevel(colour.value)} finish={currFinish} selectedColor={currentVal} />
                            </div>
                        ))}
                    </div>
                </React.Fragment>
            ))}
            {customColor && <input type="hidden" id="custom-colour-input" name="properties[Custom Colour]" value={customColor}/>}
        </StepsPanel>
    );
};

const StockCount = ({
    stock,
    finish,
    selectedColor,
}: StockCountProps) => {
    const isSilverFinish = () => {
        return finish === Finish.Silver && !selectedColor.includes('Silver');
    };
    return (
        <div 
            className={`
                option__stock-count
                ${parseInt(stock) > 0 && !isSilverFinish() ? `option__stock-count--in-stock` : null}
            `}
        >
            {!isSilverFinish() ? stock: '0'}
        </div>
    );
};